import { useEffect, useRef, useState, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { ConfirmDialogHost } from "./components/ConfirmDialog";

// Pages
import Landing from "./pages/Landing";
import AuthCallback from "./pages/AuthCallback";
// NOTE: GoogleAuthCallback was for an older flow where Google redirected the
// `?code=...` directly to the frontend. The backend now performs the token
// exchange itself and redirects here with `#session_token=...`, which
// AuthCallback already handles end-to-end (same path as magic-link).
import Dashboard from "./pages/Dashboard";
import Employees from "./pages/Employees";
import EmployeeDetail from "./pages/EmployeeDetail";
import PayGroups from "./pages/PayGroups";
import TimeEntries from "./pages/TimeEntries";
import Payments from "./pages/Payments";
import Shifts from "./pages/Shifts";
import Schedule from "./pages/Schedule";
import Settings from "./pages/Settings";
import Team from "./pages/Team";
import AuditLog from "./pages/AuditLog";
import AcceptInvite from "./pages/AcceptInvite";
import TimeClock from "./pages/TimeClock";
import MyPayments from "./pages/MyPayments";
import AdminPortal from "./pages/AdminPortal";
import AdminOverview from "./pages/AdminOverview";
import AdminTenantDetail from "./pages/AdminTenantDetail";
import AdminRoadmap from "./pages/AdminRoadmap";
import AdminFeatureRequests from "./pages/AdminFeatureRequests";
import AdminChurnInsights from "./pages/AdminChurnInsights";
import AdminPlans from "./pages/AdminPlans";
import AdminDiscounts from "./pages/AdminDiscounts";
import MagicLinkConsume from "./pages/MagicLinkConsume";
import ResetPassword from "./pages/ResetPassword";
import AccountSettings from "./pages/AccountSettings";
import { UnsavedGuardProvider } from "./lib/unsavedGuard";
import { PlanUsageProvider } from "./components/PlanUsage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Create axios instance with auth header interceptor
const apiClient = axios.create({
  baseURL: API
});

// Add auth token to all requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await apiClient.get('/auth/me');
      setUser(response.data);
      
      // Get tenant info
      const tenantRes = await apiClient.get('/tenants/current');
      setTenant(tenantRes.data.tenant);
    } catch (error) {
      // Token invalid, clear it
      localStorage.removeItem('session_token');
      setUser(null);
      setTenant(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from an OAuth callback or magic-link redirect,
    // skip the /me check. AuthCallback will exchange/store the session token
    // and trigger a full reload — until then there's nothing for us to do.
    // Without this guard, on slow mobile devices AuthProvider would race
    // AuthCallback, read empty localStorage, mark the user as logged-out,
    // and render Landing — kicking the user back to the homepage right after
    // they successfully signed in with Google.
    //
    // We check BOTH hash and search because iOS Safari strips fragments
    // from 302-redirect Locations, so the backend now also writes the token
    // as a query param as a safety net.
    const hash = window.location.hash || "";
    const search = window.location.search || "";
    if (
      hash.includes("session_id=") ||
      hash.includes("session_token=") ||
      search.includes("session_token=")
    ) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (sessionId) => {
    try {
      // Don't use withCredentials for this request - we'll get the token and store it in localStorage
      const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
      const sessionToken = response.data.session_token;
      
      // Store token in localStorage
      if (sessionToken) {
        localStorage.setItem('session_token', sessionToken);
      }
      
      setUser(response.data.user);
      
      // Get tenant info
      const tenantRes = await apiClient.get('/tenants/current');
      setTenant(tenantRes.data.tenant);
      
      return response.data.user;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    // Revoke the trusted-device token FIRST so a sign-out that races with
    // the Landing mount effect can't silently re-authenticate the user.
    const deviceToken = localStorage.getItem('device_token');
    try {
      if (deviceToken) {
        await apiClient.post('/auth/otp/revoke-device', { device_token: deviceToken });
      }
    } catch (error) {
      // Non-fatal — still clear locally.
    }
    localStorage.removeItem('device_token');
    // Set an in-flight flag so the Landing device-signin useEffect skips
    // this tick even before session_token is cleared / user state resets.
    sessionStorage.setItem('just_signed_out', '1');
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem('session_token');
      setUser(null);
      setTenant(null);
    }
  };

  const refreshTenant = async () => {
    try {
      const tenantRes = await apiClient.get('/tenants/current');
      setTenant(tenantRes.data.tenant);
    } catch (error) {
      console.error("Failed to refresh tenant:", error);
    }
  };

  // Called by AuthCallback after a successful /auth/me with a brand-new
  // session_token. Sets state in memory and pulls tenant info so we can
  // navigate to /dashboard WITHOUT a full page reload. The reload path
  // races iPhone Safari's localStorage flush — Safari can lose the token
  // between window.location.replace() and the next page's checkAuth(),
  // leaving the user looking signed-out after a successful Google login.
  const hydrateAfterLogin = async (userObj) => {
    setUser(userObj);
    try {
      const tenantRes = await apiClient.get('/tenants/current');
      setTenant(tenantRes.data.tenant);
    } catch (e) {
      // Tenant fetch failures are non-fatal — Dashboard pages will retry.
    }
    setLoading(false);
  };

  // Auto-resume a pending Stripe checkout right after auth completes.
  // Set by UpgradeButton when an unauthenticated visitor clicks Upgrade
  // on the landing page; we redirect them to sign in and pick up here.
  //
  // We wait for BOTH `user` and `tenant.tenant_id` because /checkout requires
  // a tenant — brand-new signups need to complete the company-setup dialog
  // first. The pending intent stays in localStorage until the tenant lands.
  useEffect(() => {
    if (!user) return;
    if (!tenant?.tenant_id) return;
    const stashed = localStorage.getItem("pending_checkout");
    if (!stashed) return;
    let payload;
    try { payload = JSON.parse(stashed); } catch { payload = null; }
    if (!payload || !payload.plan) {
      localStorage.removeItem("pending_checkout");
      return;
    }
    // Stale-guard: ignore anything older than 15 minutes so an abandoned
    // pending intent doesn't hijack a returning user's dashboard visit.
    if (payload.stashed_at && Date.now() - payload.stashed_at > 15 * 60 * 1000) {
      localStorage.removeItem("pending_checkout");
      return;
    }
    // Clear FIRST so a checkout failure doesn't infinitely retry.
    localStorage.removeItem("pending_checkout");
    (async () => {
      try {
        const res = await apiClient.post('/subscription/checkout', {
          origin_url: window.location.origin,
          cancel_path: "/dashboard",
          success_path: "/dashboard",
          plan: payload.plan,
          billing_cycle: payload.billing_cycle || "monthly",
          discount_code: payload.discount_code || undefined,
        });
        window.location.href = res.data.url;
      } catch (e) {
        console.error("Pending checkout failed:", e);
      }
    })();
  }, [user, tenant?.tenant_id]);

  return (
    <AuthContext.Provider value={{ user, tenant, loading, login, logout, checkAuth, refreshTenant, hydrateAfterLogin, setUser, setTenant }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading, checkAuth } = useAuth();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    if (location.state?.user) {
      setIsChecking(false);
      return;
    }
    const verify = async () => {
      await checkAuth();
      setIsChecking(false);
    };
    verify();
  }, [location.state, checkAuth]);

  if (loading || isChecking) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user && !location.state?.user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
};

// Role-based route protection — redirects unauthorized roles to /dashboard
const RoleRoute = ({ children, allowedRoles }) => {
  const { user } = useAuth();
  if (!user || !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

function AppRouter() {
  const location = useLocation();
  const { checkAuth } = useAuth();
  
  // Handle session token from backend redirect (Google OAuth callback)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("session_token");
    if (token) {
      localStorage.setItem("session_token", token);
      // Clean URL
      window.history.replaceState({}, "", location.pathname);
      checkAuth();
    }
  }, [location.search, checkAuth]);
  
  // Check URL fragment for session_id (Emergent Auth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/clock/:clockCode" element={<TimeClock />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/magic-link" element={<MagicLinkConsume />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/account" element={<AccountSettings />} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/employees" element={<ProtectedRoute><RoleRoute allowedRoles={["owner", "manager"]}><Employees /></RoleRoute></ProtectedRoute>} />
      <Route path="/employees/:employeeId" element={<ProtectedRoute><RoleRoute allowedRoles={["owner", "manager"]}><EmployeeDetail /></RoleRoute></ProtectedRoute>} />
      <Route path="/pay-groups" element={<ProtectedRoute><RoleRoute allowedRoles={["owner", "manager"]}><PayGroups /></RoleRoute></ProtectedRoute>} />
      <Route path="/time-entries" element={<ProtectedRoute><TimeEntries /></ProtectedRoute>} />
      <Route path="/payments" element={<ProtectedRoute><RoleRoute allowedRoles={["owner", "manager"]}><Payments /></RoleRoute></ProtectedRoute>} />
      <Route path="/my-payments" element={<ProtectedRoute><MyPayments /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminOverview /></ProtectedRoute>} />
      <Route path="/admin/tenants" element={<ProtectedRoute><AdminPortal /></ProtectedRoute>} />
      <Route path="/admin/tenants/:tenantId" element={<ProtectedRoute><AdminTenantDetail /></ProtectedRoute>} />
      <Route path="/admin/roadmap" element={<ProtectedRoute><AdminRoadmap /></ProtectedRoute>} />
      <Route path="/admin/feature-requests" element={<ProtectedRoute><AdminFeatureRequests /></ProtectedRoute>} />
      <Route path="/admin/plans" element={<ProtectedRoute><AdminPlans /></ProtectedRoute>} />
      <Route path="/admin/discounts" element={<ProtectedRoute><AdminDiscounts /></ProtectedRoute>} />
      <Route path="/admin/churn" element={<ProtectedRoute><AdminChurnInsights /></ProtectedRoute>} />
      <Route path="/shifts" element={<ProtectedRoute><RoleRoute allowedRoles={["owner", "manager"]}><Shifts /></RoleRoute></ProtectedRoute>} />
      <Route path="/schedule" element={<ProtectedRoute><Schedule /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><RoleRoute allowedRoles={["owner"]}><Settings /></RoleRoute></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><RoleRoute allowedRoles={["owner", "manager"]}><Team /></RoleRoute></ProtectedRoute>} />
      <Route path="/audit-log" element={<ProtectedRoute><RoleRoute allowedRoles={["owner", "manager"]}><AuditLog /></RoleRoute></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <UnsavedGuardProvider>
          <PlanUsageProvider>
            <AppRouter />
            <Toaster richColors closeButton position="top-right" />
            <ConfirmDialogHost />
          </PlanUsageProvider>
        </UnsavedGuardProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL, apiClient };
